import React, { ChangeEvent, FC } from "react";
import { Notification, Text, Heading, TextField } from "@telia/teddy";
import _get from "lodash/get";
import _size from "lodash/size";
import { DELIVERY_NOTE_DEFAULT_MAX_LENGTH, DELIVERY_TYPE, DeliveryType } from "../../../../../../constants";
import { DeliveryMethod, InfoAlert } from "../../../../../../models";
import { PickupPoint } from "../../../../../../models/state";
import { getFormattedPrice } from "../../../../../../shared/priceFormatter";
import { Alert, RadioButtonWithLabel } from "../../../../components";
import TEXT from "../../../text-consts";
import AnnouncementText from "../../atoms/AnnouncementText/AnnouncementText";
import ConnectedPickupPointList from "../../organisms/ConnectedPickupPointList/ConnectedPickupPointList";
import ConnectedPickupPointTextField from "../../organisms/ConnectedPickupPointTextField/ConnectedPickupPointTextField";
import ConnectedShopPicker from "../../organisms/ConnectedShopPicker/ConnectedShopPicker";
import "./DeliveryMethods.less";

interface LookupMethodProps {
    alert?: InfoAlert;
    description: string;
    note?: string;
    onChange: (delivery: { code?: string; deliveryType: DeliveryType; zipCode?: string | number }) => void;
    onNoteChange: (e: string, noteLength?: number) => void;
    selectedNote?: string;
    type: DeliveryType;
}

const lookupMethod: FC<LookupMethodProps> = ({
    type,
    description,
    alert,
    note,
    onChange,
    selectedNote,
    onNoteChange,
}) => {
    switch (type) {
        case DELIVERY_TYPE.PICK_UP_POINT:
            return (
                <>
                    <div className="delivery-methods__expanded__description">
                        <Text mb="100" variant="additional-100">
                            {description}
                        </Text>
                    </div>
                    {alert && (
                        <Alert className="spacer-top" kind={alert.kind}>
                            {alert.text}
                        </Alert>
                    )}
                    <ConnectedPickupPointTextField
                        onPointSelected={(code, zipCode) => {
                            onChange({ deliveryType: type, code, zipCode });
                        }}
                    />
                    <ConnectedPickupPointList
                        onPointSelected={(code, zipCode) => {
                            onChange({ deliveryType: type, code, zipCode });
                        }}
                    />
                </>
            );
        case DELIVERY_TYPE.PICK_UP_IN_SHOP:
            return (
                <>
                    <div className="delivery-methods__expanded__description">
                        <Text
                            mb="100"
                            className="delivery-methods__expanded__description__sub-header"
                            variant="additional-100"
                        >
                            {TEXT.DELIVERY.CLICK_AND_FETCH.SUBHEADING}
                        </Text>
                        <Text mb="100" variant="additional-100">
                            {TEXT.DELIVERY.CLICK_AND_FETCH.DESCRIPTION}
                        </Text>
                    </div>
                    {alert && (
                        <Alert className="spacer-top" kind={alert.kind}>
                            {alert.text}
                        </Alert>
                    )}
                    <ConnectedShopPicker
                        onShopSelected={(shop) => {
                            onChange({ deliveryType: type, code: shop?.code });
                        }}
                    />
                </>
            );
        // Just return description and alert for the other cases
        default: {
            return (
                <>
                    <div className="delivery-methods__expanded__description">
                        <Text mb="100" variant="additional-100">
                            {description}
                        </Text>
                    </div>
                    {_get(note, "allow") && (
                        <div className="spacer-top" data-di-mask>
                            <TextField>
                                <TextField.Label>
                                    {TEXT.DELIVERY.NOTE(
                                        _get(note, "maxLength", DELIVERY_NOTE_DEFAULT_MAX_LENGTH) - _size(selectedNote),
                                    )}
                                </TextField.Label>
                                <TextField.Textarea
                                    value={selectedNote}
                                    maxLength={_get(note, "maxLength", DELIVERY_NOTE_DEFAULT_MAX_LENGTH)}
                                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                                        onNoteChange(event.target.value, _get(note, "maxLength"))
                                    }
                                />
                            </TextField>
                        </div>
                    )}
                    {alert && (
                        <Alert className="spacer-top" kind={alert.kind}>
                            {alert.text}
                        </Alert>
                    )}
                </>
            );
        }
    }
};

interface DeliveryMethodsProps {
    disabledTypes?: Partial<Record<DeliveryType, string>>;
    isDisabled: boolean;
    methods: DeliveryMethod[];
    onChange: (delivery: { code?: string; deliveryType: DeliveryType; zipCode?: string | number }) => void;
    onNoteChange: (parameter: string) => void;
    pickupPoints: PickupPoint[];
    selectedNote?: string;
    selectedType?: DeliveryType;
}

const DeliveryMethods: FC<DeliveryMethodsProps> = ({
    disabledTypes,
    isDisabled,
    methods,
    selectedType,
    selectedNote,
    onChange,
    onNoteChange,
    pickupPoints,
}) => (
    <div className="delivery-methods">
        <AnnouncementText typeId="co_delivery_methods" />
        {methods.length === 1 && methods[0].type === DELIVERY_TYPE.PICK_UP_IN_SHOP && (
            <Notification
                type="tile"
                variant="information"
                mt="200"
                mb="200"
                className="delivery-methods__notification"
            >
                <Notification.Icon />
                <Notification.Text>Dette produktet er kun tilgjengelig for henting i butikk.</Notification.Text>
            </Notification>
        )}
        {methods.map(({ label, type, description, alert, note, price }) => {
            return (
                <div
                    key={`delivery-option-${type}`}
                    className={`delivery-methods__option${disabledTypes?.[type] ? " delivery-methods__option--disabled" : ""}`}
                >
                    <RadioButtonWithLabel
                        value={type}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            let code: string | undefined;
                            const deliveryType = event.target.value as DeliveryType;
                            if (([DELIVERY_TYPE.PICK_UP_POINT] as DeliveryType[]).includes(deliveryType)) {
                                code = _get(pickupPoints, "[0].code");
                            } else if (deliveryType === DELIVERY_TYPE.PICK_UP_IN_SHOP) {
                                code = "dummy-shop";
                            }

                            onChange({ deliveryType, code });
                            onNoteChange("");
                        }}
                        name={`delivery-methods`}
                        checked={selectedType === type}
                        hasRichContent
                        disabled={isDisabled || !!disabledTypes?.[type]}
                    >
                        <Heading mb="100" as="h3" variant="title-100" className="delivery-methods__heading">
                            {label}
                        </Heading>
                        <span>{getFormattedPrice(price)}</span>
                    </RadioButtonWithLabel>
                    {disabledTypes?.[type] && (
                        <Text mb="100" variant="additional-100" className="delivery-methods__disabled">
                            {disabledTypes[type]}
                        </Text>
                    )}
                    {selectedType === type && (
                        <div className="delivery-methods__expanded">
                            {lookupMethod({ type, description, alert, note, onChange, selectedNote, onNoteChange })}
                        </div>
                    )}
                </div>
            );
        })}
    </div>
);

export default DeliveryMethods;
